/**
 * OTA update policy for the native apps (Capgo).
 *
 * Capgo's own default is to install a downloaded bundle the moment the app goes to
 * the BACKGROUND and reload the WebView there — so switching to WhatsApp to read an
 * OTP (or to GPay to pay) restarted the app at `/` and threw the customer off the
 * Login / Checkout screen whenever a release had shipped since their last cold start.
 * With a release every hour or two that looked like a recurring bug (2026-09-13).
 *
 * The policy here is the industry default (CodePush / Expo Updates / the stores
 * themselves): download in the background, APPLY ONLY ON THE NEXT COLD START. It
 * is expressed as a Capgo "kill" delay condition, which the plugin clears in its
 * native `load()` on every launch (before any JS runs) and otherwise honours in
 * `installNext` on every background transition.
 *
 * TIMING MATTERS. The launch-time check is what applies a bundle that was waiting
 * from the previous session (the plugin does NOT switch bundles natively before
 * the WebView loads — it runs its `/updates` check, sees the bundle is already
 * downloaded, and installs it directly). If the "kill" condition were re-added
 * before that check finished, the plugin would report "update delayed until delay
 * conditions met" and the bundle would wait ANOTHER cold start. So the condition
 * is (re)set only from the plugin's `appReady` event — fired at the end of every
 * check (found nothing / installed / downloaded-for-later / failed), retained
 * until a listener consumes it, and re-fired after every foreground check, which
 * keeps the condition in place ahead of every background.
 *
 * Pure with respect to Capacitor: takes the updater object so it can be unit-
 * tested with a fake (`utils/otaUpdatePolicy.test.js`). Never throws — losing the
 * policy just means the plugin falls back to its default behaviour.
 */

/** The delay conditions that hold a pending bundle until the next cold start. */
export const OTA_DELAY_CONDITIONS = [{ kind: 'kill' }];

/**
 * Ask Capgo to hold any pending bundle until the app is next killed and relaunched.
 * Idempotent (the plugin replaces its condition list). Resolves `true` on success,
 * `false` when the plugin refused or is absent — never rejects.
 */
export async function holdPendingBundlesUntilRestart(updater) {
  if (!updater || typeof updater.setMultiDelay !== 'function') return false;
  try {
    await updater.setMultiDelay({ delayConditions: OTA_DELAY_CONDITIONS });
    return true;
  } catch (e) {
    console.warn('[capgo] could not hold pending bundles until restart', e?.message);
    return false;
  }
}

/**
 * Install the policy: whenever the plugin reports a finished update check
 * (`appReady`), re-assert the hold. Returns the listener handle (or null) so the
 * caller can remove it on unmount.
 */
export async function installOtaUpdatePolicy(updater) {
  if (!updater || typeof updater.addListener !== 'function') return null;
  try {
    const handle = await updater.addListener('appReady', (info) => {
      console.info('[capgo] update check finished:', info?.status);
      holdPendingBundlesUntilRestart(updater);
    });
    return handle || null;
  } catch (e) {
    console.warn('[capgo] could not install the OTA update policy', e?.message);
    return null;
  }
}
